import React, { useCallback, useEffect, useState } from 'react';

import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

import { Field, FieldLabel } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Select } from 'components/ui/select';
import { get, put } from 'helpers/api';

interface KeyRow {
  key: string;
  default: string | null;
  value: string | null;
}

interface LocaleOption {
  value: string;
  label: string;
}

interface Props {
  path: string;
  owner: { type: 'company' | 'parent_company' | 'default'; id?: number };
  locales: LocaleOption[];
}

interface IndexResponse {
  locale: string;
  keys: KeyRow[];
}

function ownerParams(owner: Props['owner']): Record<string, number> {
  if (owner.type === 'default' || owner.id == null) return {};

  return owner.type === 'parent_company'
    ? { parentCompanyId: owner.id }
    : { companyId: owner.id };
}

function draftFromRows(rows: KeyRow[]): Record<string, string> {
  return rows.reduce<Record<string, string>>((acc, row) => {
    acc[row.key] = row.value ?? '';
    return acc;
  }, {});
}

const duplicateKeyMessage = (key: string): string => `"${key}" already exists — edit it in the list above.`;

// Subset of the Blueprint Alert `severity` union we use for save/load feedback.
const FeedbackSeverity = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DESTRUCTIVE: 'destructive',
} as const;

type FeedbackSeverity = (typeof FeedbackSeverity)[keyof typeof FeedbackSeverity];

const FeedbackMessage = {
  LOAD_ERROR: 'Could not load translations. Please try again.',
  SAVE_ERROR: 'Could not save translations. Please try again.',
  SAVE_SUCCESS: 'Translations saved.',
  UNADDED_KEY: 'You have an unadded key — click Add before saving, or clear the field.',
} as const;

interface Feedback {
  severity: FeedbackSeverity;
  message: string;
}

// Self-service editor for a company's template translations, mounted from both
// the email and document design editors against their own endpoint. Defaults
// (inherited from the global default / parent company) are shown read-only as a
// hint; the editable input is this owner's own override.
export function TemplateTranslations({ path, owner, locales }: Props) {
  const [locale, setLocale] = useState<string>(locales[0]?.value ?? 'en');
  const [rows, setRows] = useState<KeyRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [newKey, setNewKey] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [addError, setAddError] = useState<string>('');

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response: IndexResponse = await get(path, { params: { ...ownerParams(owner), locale } });
      setRows(response.keys);
      setDraft(draftFromRows(response.keys));
    } catch {
      setFeedback({ severity: FeedbackSeverity.DESTRUCTIVE, message: FeedbackMessage.LOAD_ERROR });
    } finally {
      setLoading(false);
    }
  }, [path, owner, locale]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleLocaleChange = (option: LocaleOption | null) => {
    if (!option) return;

    setFeedback(null);
    setLocale(option.value);
  };

  const handleValueChange = (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFeedback(null);
    setDraft(current => ({ ...current, [key]: value }));
  };

  const handleAddKey = () => {
    const key = newKey.trim();
    if (!key) return;
    if (rows.some(row => row.key === key)) {
      setAddError(duplicateKeyMessage(key));
      return;
    }

    setAddError('');
    setFeedback(null);
    setRows(current => [...current, { key, default: null, value: newValue }]);
    setDraft(current => ({ ...current, [key]: newValue }));
    setNewKey('');
    setNewValue('');
  };

  // Enter in either "Add a new key" input behaves like clicking Add.
  const handleNewKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    handleAddKey();
  };

  const handleSave = async () => {
    if (newKey.trim()) {
      setFeedback({ severity: FeedbackSeverity.WARNING, message: FeedbackMessage.UNADDED_KEY });
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      const response: IndexResponse = await put(
        path,
        { ...ownerParams(owner), locale, translations: draft },
        { verbatimKeys: ['translations'] },
      );
      setRows(response.keys);
      setDraft(draftFromRows(response.keys));
      setFeedback({ severity: FeedbackSeverity.SUCCESS, message: FeedbackMessage.SAVE_SUCCESS });
    } catch {
      setFeedback({ severity: FeedbackSeverity.DESTRUCTIVE, message: FeedbackMessage.SAVE_ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="template-translations p-3">
      <Field className="form-group mb-3" style={{ maxWidth: 240 }}>
        <FieldLabel htmlFor="template-translation-locale">Language</FieldLabel>
        <Select<LocaleOption>
          inputId="template-translation-locale"
          options={locales}
          value={locales.find(option => option.value === locale) ?? null}
          onChange={handleLocaleChange}
        />
      </Field>

      {rows.length === 0 && !loading && (
        <p className="text-muted">No template keys yet — add one below.</p>
      )}

      {rows.map(row => (
        <Field className="form-group mb-2" key={row.key}>
          <FieldLabel htmlFor={`tt-${row.key}`}>
            <code>{row.key}</code>
            {row.default && <span className="text-muted ms-2">default: {row.default}</span>}
          </FieldLabel>
          <Input
            id={`tt-${row.key}`}
            placeholder={row.default ?? ''}
            value={draft[row.key] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleValueChange(row.key, event)}
          />
        </Field>
      ))}

      <div className="border-top mt-3 pt-3">
        <p className="fw-semibold mb-2">Add a new key</p>
        <div className="d-flex align-items-end gap-2">
          <Field className="form-group mb-0 flex-grow-1">
            <FieldLabel htmlFor="template-translation-new-key">Key</FieldLabel>
            <Input
              id="template-translation-new-key"
              placeholder="e.g. greeting"
              value={newKey}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNewKey(event.target.value);
                setAddError('');
              }}
              onKeyDown={handleNewKeyDown}
            />
          </Field>
          <Field className="form-group mb-0 flex-grow-1">
            <FieldLabel htmlFor="template-translation-new-value">Translation</FieldLabel>
            <Input
              id="template-translation-new-value"
              placeholder="e.g. Hola"
              value={newValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewValue(event.target.value)}
              onKeyDown={handleNewKeyDown}
            />
          </Field>
          <Button disabled={!newKey.trim()} label="Add" variant="secondary" onClick={handleAddKey} />
        </div>
        {addError && <p className="text-danger mt-2 mb-0">{addError}</p>}
      </div>

      {feedback && (
        <Alert
          className="mt-3 mb-0"
          icon={feedback.severity === FeedbackSeverity.SUCCESS ? faCheckCircle : faExclamationTriangle}
          severity={feedback.severity}
        >
          {feedback.message}
        </Alert>
      )}

      <div className="d-flex align-items-center mt-3">
        <Button disabled={loading} label="Save translations" variant="primary" onClick={handleSave} />
      </div>
    </div>
  );
}

export default TemplateTranslations;
